//src/pages/PathManagePatient/PendingPatientTest.jsx
import {
  Box,
  Button,
  Typography,
  useTheme,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  TableCell,
  tableCellClasses,
  styled,
} from "@mui/material";
import { tokens } from "../../theme";
import Calendar from "../calendar/calendar";
import { useState, useRef, useMemo } from "react";
import React, { useEffect } from "react";
import appointmentService from "../../services/appointmentService"; // Import the service
import { format } from "date-fns";
import Refresh from "@mui/icons-material/Refresh";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import AddNewLabTest from "../path-lab/AddNewLabTest";
import EditLabTest from "../path-lab/EditLabTest";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import { getPrescriptionsWithLabTest, getOpdLabTests } from "../../services/prescriptionService";
import { updateReportStatusReject } from "../../services/pathLabTestReportService"
import { createInvoice } from "../../services/invoiceService"; // Import createInvoice
import { getLabTestById, getLabtestsList } from "../../services/labtestService"; // Import services to get lab test details
import { updatePatientPathologyReject } from "../../services/PatientPathologyService"
import InvoiceModal from "./InvoiceModal"; // Import the new InvoiceModal component
import IpdPathPatientTest from "./IpdPathPatientTest";
import OpdPrescriptionWorkflowCard from "./components/OpdPrescriptionWorkflowCard";
import IpdPatientWorkflowCard from "./components/IpdPatientWorkflowCard";
import { QUEUE_TILE_GRID_PROPS } from "./components/workflowCardLayout";
import {
  aggregatePathLabReportsFromPrescriptions,
  enrichOpdPrescriptionWithReports,
  getOpdLabTestStatus,
  getPrescriptionPathLabReports,
  groupIpdTests,
  resolvePrescriptionReports,
} from "./labTestWorkflowUtils";
import {
  buildPrescriptionLabTestIndex,
  dedupeOpdPrescriptionsForDisplay,
  dedupePrescriptionLabTests,
  isLabTestAlreadyOnPrescription,
  registerLabTestOnPrescriptionIndex,
} from "../../utils/labTestDedupeUtils";
import { billOpdPrescription } from "./opdPrescriptionBilling";
import { getAllIPDLabTests } from "../../services/PatientlabTestService";
import { getAllLabTests } from "../../services/labtestService";
import { useAuth } from "../../context/AuthContext";
import { getPatientById, getAllPatients } from "../../services/patientService";
import { getDoctorById } from "../../services/doctorService";
import { resolveIpdDoctorName } from "./ipdDoctorUtils";
import { getOrganizationId, filterByOrganization, getOrganizationName } from "../../utils/organizationUtils";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";
import {
  invalidatePatientLabBookingCache,
  listLabBookingsForPatients,
} from "../../services/patientLabBookingService";
import { createPatientBookedTestMatcher } from "../../utils/patientBookedLabTest";

/** Pending Test tab: only in-workflow OPD tests (not finished or rejected). */
const getLabRowResultStatus = (row) =>
  String(row?.resultStatus ?? row?.result_status ?? row?.status ?? "").toUpperCase();

const isPendingQueueOpdTest = (labTest) => {
  const status = getOpdLabTestStatus({
    ...labTest,
    resultStatus:
      labTest?.resultStatus ?? labTest?.result_status ?? labTest?.status ?? null,
  });
  return status === "PENDING" || status === "BILLED" || status === "COLLECTED";
};

const filterPendingQueueLabTests = (patientLabTests = []) =>
  dedupePrescriptionLabTests((patientLabTests || []).filter(isPendingQueueOpdTest));

const columns = [
  { id: "reportId", label: "Report Id" },
  { id: "testName", label: "Test Name" },
  { id: "patientName", label: "Patient Name" },
  {
    id: "result",
    label: "Result",
  },
  { id: "normalRange", label: "Normal Range" },
  { id: "reportDate", label: "Report Date" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));
const PendingPatientTest = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useAuth();
  const { hasCapability, hasPathPackageAccess, loading: capabilitiesLoading } = useSubscriptionCapabilities();

  // Check capabilities for pathdesk access — Basic package never includes Path
  const hasPathOpdCapability = hasPathPackageAccess && hasCapability("Path OPD");
  const hasPathIpdCapability =
    hasPathPackageAccess && (hasCapability("Path IPD") || hasCapability("IPD"));
  const hasQueueAccess = hasPathOpdCapability || hasPathIpdCapability;
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ path_lab_test: true });
  const [path_lab_test, setPathLabTestList] = useState([]);
  const [lab_test_info, setLabTestInfo] = useState({});
  const [open_lab_test_modal, setOpenLabReportLest] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [ipdWorkflowGroups, setIpdWorkflowGroups] = useState([]);
  const [isTestBookedByPatient, setIsTestBookedByPatient] = useState(() => () => false);
  const bookingSourceRef = useRef([]);
  const [fetchingIpd, setFetchingIpd] = useState(false);
  // const [invoiceDetails, setInvoiceDetails] = useState(null); // Add state for invoice details
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false); // State for invoice modal
  const [invoiceDetails, setInvoiceDetails] = useState({
  charges: []
  // Initialize other properties based on your structure
});

  // Load OPD + IPD queue data when capabilities allow
  useEffect(() => {
    if (capabilitiesLoading) return;

    if (!hasQueueAccess) {
      setPathLabTestList([]);
      setIpdWorkflowGroups([]);
      setFetching({ path_lab_test: false });
      return;
    }

    const orgId = getOrganizationId(user);
    if (hasPathOpdCapability && orgId) {
      fetchPathLabTestList();
    } else if (hasPathOpdCapability && !orgId) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser?.organizationId) {
            fetchPathLabTestList();
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    }

    if (hasPathIpdCapability && user?.id) {
      fetchIpdWorkflowGroups();
    }
  }, [
    user,
    user?.organizationId,
    user?.id,
    hasPathOpdCapability,
    hasPathIpdCapability,
    hasQueueAccess,
    capabilitiesLoading,
  ]);

  const fetchIpdWorkflowGroups = async () => {
    setFetchingIpd(true);
    try {
      let labTestsMap = new Map();
      try {
        const allLabTests = await getAllLabTests();
        (Array.isArray(allLabTests) ? allLabTests : []).forEach((labTest) => {
          if (labTest.id) labTestsMap.set(labTest.id, labTest);
        });
      } catch (error) {
        console.warn("[PendingPatientTest] Could not load lab tests for IPD:", error);
      }

      const rows = await getAllIPDLabTests();
      const userFilteredRows = (Array.isArray(rows) ? rows : []).filter((row) => row.labId === user?.id);

      const filteredRows = userFilteredRows.filter((row) => {
        const status = String(row.testStatus || "").toUpperCase();
        if (row.assignedBy === null) return false;
        if (row.testStatus == null || status === "PENDING") return true;
        if (status === "COLLECTED") return true;
        return false;
      });

      const normalize = (value) => (value || "").toString().trim().toLowerCase();
      const rowsWithResolvedTestNames = filteredRows.map((row) => {
        let resolvedTestName = row.testName || row.test_name || row.labTestName || row.name || "—";
        const labTestId = row.testId || row.labTestId || row.test_id;
        if (labTestId && labTestsMap.has(labTestId)) {
          const labTest = labTestsMap.get(labTestId);
          resolvedTestName =
            labTest.testName || labTest.name || labTest.test_name || labTest.labTestName || resolvedTestName;
        }
        return { ...row, resolvedTestName };
      });

      const doctorNameCache = new Map();
      const rowsWithDoctorNames = await Promise.all(
        rowsWithResolvedTestNames.map(async (row) => ({
          ...row,
          resolvedDoctorName: await resolveIpdDoctorName(row, doctorNameCache),
        }))
      );

      setIpdWorkflowGroups(groupIpdTests(rowsWithDoctorNames));
    } catch (error) {
      console.error("[PendingPatientTest] Failed to fetch IPD workflow groups:", error);
      setIpdWorkflowGroups([]);
    } finally {
      setFetchingIpd(false);
    }
  };

  // Auto-reject only after 3 calendar days so a patient can still book 1–2 days later.
  const AUTO_REJECT_AFTER_DAYS = 3;
  const autoRejectProcessed = useRef(new Set());
  useEffect(() => {
    const maybeAutoReject = async () => {
      try {
        let didAny = false;
        const reportsCache = new Map(); // patientId -> reports
        // Optionally cache lab tests by name to get labTestId when no report exists
        let allLabTests = null;
        const normalize = (s) => (s || '').toString().trim().toLowerCase();

        for (const presc of path_lab_test || []) {
          const prescId = presc?.id;
          const created = presc?.createdAt ? dayjs(presc.createdAt) : null;
          if (!prescId || !created) continue;
          // Calendar days, not elapsed hours: a test prescribed on the 10th stays
          // visible on the 11th and 12th, and is rejected starting on the 13th.
          const ageInCalendarDays = dayjs().startOf("day").diff(created.startOf("day"), "day");
          if (ageInCalendarDays < AUTO_REJECT_AFTER_DAYS) continue;

          for (const t of presc.patientLabTests || []) {
            const key = `${prescId}|${normalize(t.testName)}`;
            const statusUpper = getLabRowResultStatus(t);
            // Consider PENDING, BILLED, or COLLECTED as eligible for auto-reject after 3 calendar days
            if (!(['PENDING','BILLED','COLLECTED'].includes(statusUpper) || t.resultStatus == null)) continue;
            if (autoRejectProcessed.current.has(key)) continue;

            // Use embedded pathLabReports from /withlabtest (no per-patient API call)
            if (!reportsCache.has(presc.patientId)) {
              const patientReports = (path_lab_test || [])
                .filter((row) => Number(row?.patientId) === Number(presc.patientId))
                .flatMap((row) => getPrescriptionPathLabReports(row));
              reportsCache.set(presc.patientId, patientReports);
            }
            const reports = reportsCache.get(presc.patientId) || [];

            // Match by name, and same day as prescription
            let candidates = reports.filter((r) =>
              normalize(r.testName) === normalize(t.testName) &&
              (!getLabRowResultStatus(r) || ["PENDING", "BILLED", "COLLECTED"].includes(getLabRowResultStatus(r)))
            );
            candidates = candidates.filter((r) => {
              const rd = r.reportDate || r.createdAt || r.date;
              return rd ? dayjs(rd).isSame(created, 'day') : false;
            });
            // Pick latest
            let chosen = null;
            for (const r of candidates) {
              if (!chosen) { chosen = r; continue; }
              const rd = r.reportDate || r.createdAt || r.date;
              const cd = chosen.reportDate || chosen.createdAt || chosen.date;
              if (rd && cd && dayjs(rd).isAfter(dayjs(cd))) chosen = r;
            }

            // Attempt to update pathlab report if we found one
            if (chosen?.reportId) {
              try {
                await updateReportStatusReject(chosen.reportId);
                didAny = true;
              } catch (_) {}
            }

            // Always try to update patient_pathology using labTestId
            // Use testId from chosen report if present; else look up by name from lab tests list
            let labTestId = chosen?.testId;
            if (!labTestId) {
              if (!allLabTests) allLabTests = await getLabtestsList();
              const found = (allLabTests || []).find((lt) => normalize(lt.name) === normalize(t.testName));
              labTestId = found?.id;
            }
            if (labTestId) {
              try {
                await updatePatientPathologyReject(presc.patientId, prescId, labTestId);
                didAny = true;
              } catch (_) {}
            }
            autoRejectProcessed.current.add(key);
          }
        }
        if (didAny) await fetchPathLabTestList();
      } catch (_) {}
    };
    if (Array.isArray(path_lab_test) && path_lab_test.length) {
      // Fire and forget; no UI blocking
      maybeAutoReject();
    }
  }, [path_lab_test]);

  // const fetchPathLabTestList = async () => {
  //   setFetching({ ...fetching, path_lab_test: true });
  //   try {
  //     const data = await getPrescriptionsWithLabTest();
  //     setPathLabTestList(data);
  //   } catch (error) {
  //     console.log(error);
  //   }
  //   setFetching({ ...fetching, path_lab_test: false });
  // };

  const fetchPathLabTestList = async () => {
    invalidatePatientLabBookingCache();
    setFetching({ ...fetching, path_lab_test: true });
    try {
        // Get organizationId for logging and validation
        const organizationId = getOrganizationId(user);
        console.log(`[PendingPatientTest] Fetching with organizationId: ${organizationId}`);
        
        // Fetch prescriptions with lab tests
        // Try getPrescriptionsWithLabTest first, fallback to getOpdLabTests if needed
        let data = await getPrescriptionsWithLabTest();
        console.log(`[PendingPatientTest] getPrescriptionsWithLabTest returned ${Array.isArray(data) ? data.length : 0} prescriptions`);
        
        // If no data returned, try the alternative endpoint (same as OpdIpdPatients uses)
        if (!Array.isArray(data) || data.length === 0) {
          console.log(`[PendingPatientTest] No data from getPrescriptionsWithLabTest, trying getOpdLabTests...`);
          try {
            const alternativeData = await getOpdLabTests();
            if (Array.isArray(alternativeData) && alternativeData.length > 0) {
              console.log(`[PendingPatientTest] getOpdLabTests returned ${alternativeData.length} prescriptions`);
              data = alternativeData;
            }
          } catch (altError) {
            console.error(`[PendingPatientTest] Error fetching from getOpdLabTests:`, altError);
          }
        }
        
        console.log(`[PendingPatientTest] Final data before filtering: ${Array.isArray(data) ? data.length : 0} prescriptions`);
        
        // Log sample prescription to see its structure
        if (Array.isArray(data) && data.length > 0) {
          console.log(`[PendingPatientTest] Sample prescription:`, data[0]);
          console.log(`[PendingPatientTest] Sample prescription keys:`, Object.keys(data[0]));
          console.log(`[PendingPatientTest] Sample prescription organizationId:`, data[0]?.organizationId);
        }
        
        // Apply organization filtering
        // Since backend doesn't include organizationId field and might not filter correctly,
        // we need to verify prescriptions belong to organization by checking patientIds
        // Declare orgPatientIds at function scope so it can be used later for report filtering
        let orgPatientIds = new Set();
        if (Array.isArray(data) && data.length > 0) {
          if (organizationId) {
            const hasOrgIdField = data.some(p => p?.organizationId != null || p?.organization_id != null);
            
            if (!hasOrgIdField) {
              // No prescriptions have organizationId field - verify by checking patientIds
              console.warn(`[PendingPatientTest] ⚠️ Received ${data.length} prescriptions without organizationId field.`);
              console.log(`[PendingPatientTest] Verifying prescriptions belong to organization ${organizationId} by checking patientIds...`);
              
              try {
                // Fetch organization-specific patients to verify prescriptions
                const orgPatients = await getAllPatients();
                orgPatientIds = new Set();
                
                // Filter patients by organization and collect their IDs
                // Note: getAllPatients should return org-specific patients when organizationId is passed
                const filteredPatients = filterByOrganization(orgPatients, user);
                filteredPatients.forEach(patient => {
                  if (patient?.id) {
                    orgPatientIds.add(Number(patient.id)); // Ensure numeric comparison
                  }
                });
                
                console.log(`[PendingPatientTest] Found ${orgPatientIds.size} patients in organization ${organizationId}`);
                if (orgPatientIds.size > 0) {
                  console.log(`[PendingPatientTest] Sample patient IDs:`, Array.from(orgPatientIds).slice(0, 5));
                }
                
                // Filter prescriptions: only keep those whose patientId exists in organization's patients
                const originalCount = data.length;
                const patientIdsInPrescriptions = new Set(data.map(p => Number(p?.patientId)).filter(Boolean));
                console.log(`[PendingPatientTest] Unique patientIds in prescriptions: ${patientIdsInPrescriptions.size}`);
                console.log(`[PendingPatientTest] Sample patientIds from prescriptions:`, Array.from(patientIdsInPrescriptions).slice(0, 5));
                
                // Check overlap
                const matchingPatientIds = Array.from(patientIdsInPrescriptions).filter(id => orgPatientIds.has(id));
                console.log(`[PendingPatientTest] Matching patientIds: ${matchingPatientIds.length}`);
                
                data = data.filter(presc => {
                  const patientId = presc?.patientId ? Number(presc.patientId) : null;
                  if (!patientId) return false;
                  
                  const belongsToOrg = orgPatientIds.has(patientId);
                  return belongsToOrg;
                });
                
                console.log(`[PendingPatientTest] Filtered ${originalCount} prescriptions to ${data.length} belonging to organization ${organizationId}`);
              } catch (patientError) {
                console.error(`[PendingPatientTest] Error verifying patients:`, patientError);
                // If we can't verify and we have organizationId, exclude all as safety measure
                console.warn(`[PendingPatientTest] ⚠️ Cannot verify prescriptions belong to organization. Excluding all as safety measure.`);
                data = [];
              }
            } else {
              // Some prescriptions have organizationId - use filterByOrganization to filter properly
              data = filterByOrganization(data, user);
              console.log(`[PendingPatientTest] After filterByOrganization: ${data.length} prescriptions`);
            }
          } else {
            // No organizationId - use filterByOrganization utility for standard filtering
            data = filterByOrganization(data, user);
            console.log(`[PendingPatientTest] After filterByOrganization: ${data.length} prescriptions`);
          }
        } else if (!Array.isArray(data)) {
          console.warn(`[PendingPatientTest] ⚠️ Data is not an array:`, typeof data, data);
          data = [];
        }

        bookingSourceRef.current = (Array.isArray(data) ? data : []).map((prescription) => ({
          id: prescription?.id,
          patientId: prescription?.patientId,
          patientName: prescription?.patientName,
          doctorId: prescription?.doctorId,
          doctorName: prescription?.doctorName,
          createdAt: prescription?.createdAt,
          patientLabTests: prescription?.patientLabTests || [],
        }));

        // Enrich with embedded pathLabReports from /withlabtest (single API response)
        data = (Array.isArray(data) ? data : []).map((prescription) => {
          const enriched = enrichOpdPrescriptionWithReports(prescription);
          return {
            ...enriched,
            patientLabTests: filterPendingQueueLabTests(enriched.patientLabTests),
            pathLabReports: (enriched.pathLabReports || []).filter((report) =>
              isPendingQueueOpdTest({
                resultStatus: report.resultStatus ?? report.result_status ?? report.status,
              })
            ),
          };
        });
        
        // Build report list from embedded data (replaces GET /pathlabreports)
        let allReports = aggregatePathLabReportsFromPrescriptions(data);
        try {
          allReports = Array.isArray(allReports) ? allReports : [];
          
          // Filter reports by organization to ensure only organization-specific data is shown
          allReports = filterByOrganization(allReports, user);
          console.log(`[PendingPatientTest] Embedded pathLabReports after filterByOrganization: ${allReports.length} reports`);
          
          // Additional verification: filter reports by patient ID to ensure they belong to organization
          if (organizationId && allReports.length > 0) {
            // Get organization patient IDs if not already available
            if (orgPatientIds.size === 0) {
              try {
                const orgPatients = await getAllPatients();
                const filteredPatients = filterByOrganization(orgPatients, user);
                orgPatientIds = new Set();
                filteredPatients.forEach(patient => {
                  if (patient?.id) {
                    orgPatientIds.add(Number(patient.id));
                  }
                });
                console.log(`[PendingPatientTest] Found ${orgPatientIds.size} organization patients for report filtering`);
              } catch (patientError) {
                console.error(`[PendingPatientTest] Error fetching patients for report filtering:`, patientError);
              }
            }
            
            if (orgPatientIds.size > 0) {
              const originalReportCount = allReports.length;
              allReports = allReports.filter(report => {
                const reportPatientId = report?.patientId || report?.patient_id;
                if (!reportPatientId) return false;
                const belongsToOrg = orgPatientIds.has(Number(reportPatientId));
                if (!belongsToOrg) {
                  console.warn(`[PendingPatientTest] Excluding report with patientId ${reportPatientId} - not in organization patients`);
                }
                return belongsToOrg;
              });
              console.log(`[PendingPatientTest] Filtered reports by patient ID: ${originalReportCount} to ${allReports.length} reports`);
            } else if (organizationId && allReports.length > 0) {
              // If we can't verify patients, exclude all reports as safety measure
              console.warn(`[PendingPatientTest] ⚠️ No organization patients found. Excluding all reports as safety measure.`);
              allReports = [];
            }
          }
        } catch (reportError) {
          console.error("Error processing embedded path lab reports:", reportError);
          allReports = [];
        }
        
        // Create a map of prescriptionId -> prescription for quick lookup
        const prescMap = new Map();
        (Array.isArray(data) ? data : []).forEach(presc => {
          if (presc?.id) {
            prescMap.set(presc.id, presc);
          }
        });
        console.log(`[PendingPatientTest] Created prescMap with ${prescMap.size} prescriptions`);
        
        // Create a map of test names in each prescription to avoid duplicates
        const normalize = (s) => (s || '').toString().trim().toLowerCase();
        const prescTestMap = new Map(); // prescriptionId -> { testNames, labTestIds }
        prescMap.forEach((presc, prescId) => {
          presc.patientLabTests = filterPendingQueueLabTests(presc.patientLabTests);
          prescTestMap.set(prescId, buildPrescriptionLabTestIndex(presc.patientLabTests));
        });
        
        // Process reports: add tests from patient_pathology that aren't in prescription's patientLabTests
        // Also handle reports with prescriptionId that don't have a matching prescription
        const reportsByPrescId = new Map(); // prescriptionId -> reports[]
        const reportsWithoutPresc = []; // Reports without prescriptionId or with non-matching prescriptionId
        
        for (const report of allReports) {
          const reportPrescId = report.prescriptionId;
          const reportStatus = getLabRowResultStatus(report);
          
          // Only process reports with valid status
          if (!['PENDING', 'BILLED', 'COLLECTED', ''].includes(reportStatus)) continue;
          
          // Verify report's patientId belongs to organization (if we have orgPatientIds)
          if (organizationId && orgPatientIds.size > 0) {
            const reportPatientId = report?.patientId || report?.patient_id;
            if (reportPatientId) {
              const numericPatientId = Number(reportPatientId);
              if (!orgPatientIds.has(numericPatientId)) {
                console.warn(`[PendingPatientTest] Skipping report with patientId ${reportPatientId} - not in organization patients`);
                continue;
              }
            } else {
              console.warn(`[PendingPatientTest] Skipping report without patientId:`, report);
              continue;
            }
          }
          
          if (reportPrescId) {
            // Report has prescriptionId - group by prescriptionId
            // Also verify the prescription belongs to the organization (check if it's in prescMap)
            if (!reportsByPrescId.has(reportPrescId)) {
              reportsByPrescId.set(reportPrescId, []);
            }
            reportsByPrescId.get(reportPrescId).push(report);
          } else {
            // Report doesn't have prescriptionId - might be created from patient dashboard
            reportsWithoutPresc.push(report);
          }
        }
        
        // Process reports for existing prescriptions
        for (const [reportPrescId, reports] of reportsByPrescId.entries()) {
          const presc = prescMap.get(reportPrescId);
          if (!presc) {
            // Prescription not found in our filtered list - add reports to reportsWithoutPresc
            // so we can create virtual prescriptions from them
            reportsWithoutPresc.push(...reports);
            continue;
          }
          
          const labTestIndex =
            prescTestMap.get(reportPrescId) || buildPrescriptionLabTestIndex();

          for (const report of reports) {
            if (isLabTestAlreadyOnPrescription(labTestIndex, report)) {
              continue;
            }

            const testName = report.testName || "";
            if (!presc.patientLabTests) presc.patientLabTests = [];

            const labTestEntry = {
              testName,
              labTestId: report.labTestId || report.testId,
              resultStatus: report.resultStatus ?? report.result_status ?? report.status ?? "PENDING",
              result: report.result || null,
              normalRange: report.normalRange || null,
            };

            presc.patientLabTests.push(labTestEntry);
            registerLabTestOnPrescriptionIndex(labTestIndex, labTestEntry);
          }

          prescTestMap.set(reportPrescId, labTestIndex);
        }
        
        // Create virtual prescriptions from reports that don't have matching prescriptions
        // Group reports by patientId to create virtual prescriptions
        // IMPORTANT: Also verify patientId belongs to organization and validate patient data consistency
        // NOTE: We deliberately DO NOT try to attach these reports to existing prescriptions.
        // For patients who already have prescriptions, we skip creating virtual prescriptions later.
        // This avoids incorrectly adding unrelated tests (like \"testing 15/12/25\") to a real prescription.
        const reportsByPatientId = new Map();
        for (const report of reportsWithoutPresc) {
          const patientId = report.patientId;
          if (!patientId) {
            console.warn(`[PendingPatientTest] Skipping report without patientId:`, report);
            continue;
          }

          // Verify patientId belongs to organization (if we have orgPatientIds)
          if (organizationId && orgPatientIds.size > 0) {
            const numericPatientId = Number(patientId);
            if (!orgPatientIds.has(numericPatientId)) {
              console.warn(
                `[PendingPatientTest] Skipping report with patientId ${patientId} - not in organization patients`
              );
              continue;
            }
          }

          if (!reportsByPatientId.has(patientId)) {
            reportsByPatientId.set(patientId, []);
          }
          reportsByPatientId.get(patientId).push(report);
        }
        
        // Create virtual prescriptions from reports grouped by patientId
        // These are reports that couldn't be matched to existing prescriptions
        // IMPORTANT: If there's already a prescription for this patient, skip creating virtual prescription
        // to avoid showing duplicate/incorrect tests. Only create virtual prescriptions for truly orphaned reports.
        const virtualPrescriptionsToCreate = [];
        for (const [patientId, reports] of reportsByPatientId.entries()) {
          // Check if there's already a prescription for this patient
          const hasExistingPresc = Array.from(prescMap.values()).some(presc => 
            presc.patientId === patientId || Number(presc.patientId) === Number(patientId)
          );
          
          if (hasExistingPresc) {
            console.log(`[PendingPatientTest] Skipping virtual prescription for patientId ${patientId} - already has existing prescription(s). Reports will not be shown separately.`);
            console.log(`[PendingPatientTest] Skipped reports:`, reports.map(r => r.testName || 'N/A'));
            continue; // Skip creating virtual prescription if patient already has a prescription
          }
          
          virtualPrescriptionsToCreate.push([patientId, reports]);
        }
        
        // Create virtual prescriptions from reports grouped by patientId
        // These are reports that couldn't be matched to existing prescriptions AND don't have existing prescriptions
        // Fetch patient and doctor info for better display
        // IMPORTANT: Validate that all reports in a group have consistent patient data
        for (const [patientId, reports] of virtualPrescriptionsToCreate) {
          const firstReport = reports[0];
          
          // Log report fields to debug
          console.log(`[PendingPatientTest] Processing reports for patientId ${patientId}, ${reports.length} reports`);
          console.log(`[PendingPatientTest] Report keys:`, Object.keys(firstReport));
          console.log(`[PendingPatientTest] Report doctorId: ${firstReport.doctorId}, doctorName: ${firstReport.doctorName}`);
          
          // Validate patient data consistency across all reports in this group
          const normalizeName = (name) => (name || '').toString().trim().toLowerCase();
          const firstPatientName = normalizeName(firstReport.patientName);
          const inconsistentReports = reports.filter(r => {
            const rPatientId = r.patientId;
            const rPatientName = normalizeName(r.patientName);
            // Check if patientId matches and patientName is consistent (if both are present)
            if (rPatientId !== patientId) {
              console.warn(`[PendingPatientTest] ⚠️ Inconsistent patientId in report group: expected ${patientId}, found ${rPatientId}`);
              return true;
            }
            if (firstPatientName && rPatientName && firstPatientName !== rPatientName) {
              console.warn(`[PendingPatientTest] ⚠️ Inconsistent patientName in report group: expected "${firstReport.patientName}", found "${r.patientName}"`);
              return true;
            }
            return false;
          });
          
          if (inconsistentReports.length > 0) {
            console.error(`[PendingPatientTest] ⚠️ Found ${inconsistentReports.length} reports with inconsistent patient data for patientId ${patientId}. Excluding them.`);
            // Filter out inconsistent reports
            const consistentReports = reports.filter(r => !inconsistentReports.includes(r));
            if (consistentReports.length === 0) {
              console.warn(`[PendingPatientTest] No consistent reports remaining for patientId ${patientId}. Skipping virtual prescription.`);
              continue;
            }
            reports.splice(0, reports.length, ...consistentReports);
          }
          
          // Fetch patient info if not available in report
          let patientName = firstReport.patientName || 'Unknown Patient';
          try {
            if (!firstReport.patientName && patientId) {
              const patient = await getPatientById(patientId);
              if (patient?.name || (patient?.firstName && patient?.lastName)) {
                patientName = patient.name || `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Unknown Patient';
              }
            } else if (firstReport.patientName) {
              // Use patientName from report, but validate it matches the fetched patient
              try {
                const patient = await getPatientById(patientId);
                if (patient?.name || (patient?.firstName && patient?.lastName)) {
                  const fetchedName = patient.name || `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
                  if (fetchedName && normalizeName(fetchedName) !== normalizeName(firstReport.patientName)) {
                    console.warn(`[PendingPatientTest] ⚠️ Patient name mismatch for patientId ${patientId}: report says "${firstReport.patientName}", database says "${fetchedName}". Using database value.`);
                    patientName = fetchedName;
                  } else {
                    patientName = firstReport.patientName;
                  }
                }
              } catch (e) {
                // If we can't fetch, use the name from report
                patientName = firstReport.patientName;
              }
            }
          } catch (patientError) {
            console.warn(`[PendingPatientTest] Could not fetch patient ${patientId}:`, patientError);
          }
          
          // Fetch doctor info if doctorId is available
          let doctorName = firstReport.doctorName || 'Unknown Doctor';
          const doctorId = firstReport.doctorId;
          
          if (doctorId) {
            try {
              console.log(`[PendingPatientTest] Fetching doctor with ID: ${doctorId}`);
              const doctor = await getDoctorById(doctorId);
              console.log(`[PendingPatientTest] Fetched doctor:`, doctor);
              
              if (doctor) {
                if (doctor.name) {
                  doctorName = doctor.name;
                } else if (doctor.firstName || doctor.lastName) {
                  const firstName = doctor.firstName || '';
                  const lastName = doctor.lastName || '';
                  doctorName = `Dr. ${firstName} ${lastName}`.trim();
                  if (!doctorName || doctorName === 'Dr.') {
                    doctorName = 'Unknown Doctor';
                  }
                }
                console.log(`[PendingPatientTest] Resolved doctor name: ${doctorName}`);
              } else {
                console.warn(`[PendingPatientTest] Doctor ${doctorId} not found or returned null`);
              }
            } catch (doctorError) {
              console.warn(`[PendingPatientTest] Could not fetch doctor ${doctorId}:`, doctorError);
              console.warn(`[PendingPatientTest] Doctor error details:`, doctorError.response?.data || doctorError.message);
            }
          } else {
            console.warn(`[PendingPatientTest] No doctorId in report for patientId ${patientId}`);
          }
          
          // Create a virtual prescription from the reports
          const virtualPresc = {
            id: `virtual_${patientId}_${Date.now()}`, // Virtual ID
            patientId: patientId,
            patientName: patientName,
            doctorId: doctorId || null,
            doctorName: doctorName,
            createdAt: firstReport.createdAt || firstReport.reportDate || new Date().toISOString(),
            patientLabTests: filterPendingQueueLabTests(
              reports.map((report) => ({
                __entity: "patientLabTest",
                testName: report.testName || report.test_name || report.name || "",
                labTestId: report.labTestId || report.testId,
                testId: report.testId || report.labTestId,
                reportId: report.reportId ?? report.id ?? null,
                resultStatus: report.resultStatus || "PENDING",
                result: report.result || null,
                normalRange: report.normalRange || null,
                reportDate: report.reportDate || report.createdAt || null,
                patientId: report.patientId || patientId,
              }))
            ),
          };
          
          prescMap.set(virtualPresc.id, virtualPresc);
          console.log(`[PendingPatientTest] Created virtual prescription for patientId ${patientId} (${patientName}) with doctor ${doctorName} and ${reports.length} tests`);
          console.log(`[PendingPatientTest] Virtual prescription test names:`, reports.map(r => r.testName || 'N/A'));
        }
        
        // Include tests with resultStatus: null, PENDING, BILLED, or COLLECTED
        const allPrescriptions = Array.from(prescMap.values());
        console.log(`[PendingPatientTest] Total prescriptions in map: ${allPrescriptions.length}`);
        
        // Log sample prescription to check patientLabTests
        if (allPrescriptions.length > 0) {
          const sample = allPrescriptions[0];
          console.log(`[PendingPatientTest] Sample prescription ID: ${sample.id}, patientLabTests count: ${(sample.patientLabTests || []).length}`);
          if ((sample.patientLabTests || []).length > 0) {
            console.log(`[PendingPatientTest] Sample lab test statuses:`, (sample.patientLabTests || []).map(t => t.resultStatus || 'null'));
          }
        }
        
        // Only show prescriptions with in-workflow tests (exclude REJECT / COMPLETE)
        const queuePrescriptions = allPrescriptions
          .map((prescription) => ({
            ...prescription,
            patientLabTests: filterPendingQueueLabTests(prescription.patientLabTests),
          }))
          .filter((prescription) => (prescription.patientLabTests || []).length > 0);

        console.log(`[PendingPatientTest] After resultStatus filtering: ${queuePrescriptions.length} prescriptions`);
        const sorted = dedupeOpdPrescriptionsForDisplay(
          [...queuePrescriptions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        );
        console.log(`[PendingPatientTest] Final list to display: ${sorted.length} prescriptions`);
        setPathLabTestList(sorted);
    } catch (error) {
        console.log(error);
    }
    setFetching({ ...fetching, path_lab_test: false });
};

  // const handleAccept = async (test) => {
  //   try {
  //     // Step 1: Fetch the lab test report
  //     const reports = await getPathPabReportByPatient(test.patientId);
  //     const foundReport = reports.find(report => report.testName === test.patientLabTests[0]?.testName);
      
  //     if (!foundReport) {
  //       console.error("No matching report found for the selected test:", test);
  //       return; // Exit if no report is found
  //     }
  //     console.log("Matching Report:", foundReport);
  //     setMatchingReport(foundReport); // Store the matching report
  //     setSelectedTest({ ...test, reportId: foundReport.reportId }); // Set selected test with report ID
       
  //     // Step 2: Create an invoice for the lab tests
  //     const invoiceData = {
  //       patientId: test.patientId,
  //       particulars: "Lab Tests",
  //       invoiceType: "PATHOLOGYTEST",
  //       fromName: "Aryan Hospital",
  //       totalAmount: 0, // Initialize total amount
  //       labTestIds: test.patientLabTests.map(labTest => labTest.labTestId), // Assuming labTestId is available
  //     };
  //     // Calculate total amount based on lab tests
  //     const labTests = await Promise.all(
  //       invoiceData.labTestIds.map(id => getLabTestById(id)) // Use the imported function to get lab test details
  //     );
  //     invoiceData.totalAmount = labTests.reduce((total, labTest) => total + labTest.amount, 0);
  //     // Create the invoice
  //     const createdInvoice = await createInvoice(invoiceData); // Ensure createInvoice is imported
  //     console.log("Invoice created:", createdInvoice);

  //     // Store the created invoice details
  //       setInvoiceDetails(createdInvoice);

  //     // Step 3: Open the CollectSampleModal
  //     setOpenCollectSampleModal(true); // Open the modal for sample collection
  //   } catch (error) {
  //     console.error("Error in handleAccept:", error);
  //   }
  // };

  const handleAccept = async (prescription) => {
    try {
      const result = await billOpdPrescription(prescription, user);
      if (!result) return;
      setInvoiceDetails(result.invoiceDetails);
      setSelectedTest(result.selectedTest);
      setOpenInvoiceModal(true);
    } catch (error) {
      console.error("Error in handleAccept:", error);
      alert("Error preparing lab test invoice. Please check the console for details.");
    }
  };

// const handleReject = async (reportId) => {
//     try {
//         await updateReportStatusReject(reportId); // Call the service to update the report status
//         // Optionally, you can refresh the list of tests after rejection
//         fetchPathLabTestList();
//     } catch (error) {
//         console.error("Error rejecting report:", error);
//     }
// };

const handleReject = async (prescription) => {
    try {
        if (!prescription) {
            console.error("No prescription provided for rejection.");
            return;
        }

        // Use embedded pathLabReports from /withlabtest (fallback to API for older backend)
        const reports =
          getPrescriptionPathLabReports(prescription).length > 0
            ? getPrescriptionPathLabReports(prescription)
            : await resolvePrescriptionReports(prescription);

        // Consider only pending/null tests on this prescription
        const pendingTests = (prescription.patientLabTests || []).filter(
            (t) => t.resultStatus === 'PENDING' || t.resultStatus == null
        );
        if (pendingTests.length === 0) {
            console.error("No pending tests found for this prescription.");
            return;
        }

        const normalize = (s) => (s || '').toString().trim().toLowerCase();
        const pendingNames = pendingTests.map((pt) => normalize(pt.testName));

        // Match reports by name and pending status, same day as prescription
        let candidateReports = reports.filter(
            (r) => pendingNames.includes(normalize(r.testName)) && (r.resultStatus === null || r.resultStatus === 'PENDING')
        );

        // Constrain strictly to the same day as prescription date
        const prescDate = prescription.createdAt ? dayjs(prescription.createdAt) : null;
        if (prescDate) {
            candidateReports = candidateReports.filter((r) => {
                const rd = r.reportDate || r.createdAt || r.date;
                return rd ? dayjs(rd).isSame(prescDate, 'day') : false;
            });
        }

        if (candidateReports.length === 0) {
            console.error("No matching reports found for rejection.");
            return;
        }

        // For each test, pick the latest report within that day
        const byName = new Map();
        for (const r of candidateReports) {
            const key = normalize(r.testName);
            const current = byName.get(key);
            const rDate = r.reportDate || r.createdAt || r.date;
            const rMoment = rDate ? dayjs(rDate) : null;
            if (!current) {
                byName.set(key, r);
            } else {
                const cDate = current.reportDate || current.createdAt || current.date;
                const cMoment = cDate ? dayjs(cDate) : null;
                if (rMoment && cMoment && rMoment.isAfter(cMoment)) {
                    byName.set(key, r);
                }
            }
        }
        const matchedReports = Array.from(byName.values());

        // Apply rejection for each matched report
        for (const r of matchedReports) {
            await updateReportStatusReject(r.reportId);
            await updatePatientPathologyReject(prescription.patientId, prescription.id, r.testId);
        }

        // Refresh the list
        fetchPathLabTestList();
    } catch (error) {
        console.error("Error rejecting report:", error);
    }
};

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getOpdQueueSortTime = (prescription) => {
    const created = prescription?.createdAt;
    return created ? dayjs(created).valueOf() : 0;
  };

  const getIpdQueueSortTime = (group) => {
    const testDate = group?.testDate;
    if (!testDate) return 0;
    if (Array.isArray(testDate)) {
      return dayjs(
        new Date(testDate[0], (testDate[1] || 1) - 1, testDate[2] || 1)
      ).valueOf();
    }
    return dayjs(testDate).valueOf();
  };

  const combinedQueueItems = useMemo(() => {
    const items = [];
    if (hasPathOpdCapability) {
      (path_lab_test || []).forEach((prescription) => {
        items.push({
          type: "opd",
          key: `opd-${prescription.id}`,
          prescription,
          sortTime: getOpdQueueSortTime(prescription),
        });
      });
    }
    if (hasPathIpdCapability) {
      (ipdWorkflowGroups || []).forEach((group) => {
        items.push({
          type: "ipd",
          key: `ipd-${group.key}`,
          group,
          sortTime: getIpdQueueSortTime(group),
        });
      });
    }
    return items.sort((a, b) => b.sortTime - a.sortTime);
  }, [
    path_lab_test,
    ipdWorkflowGroups,
    hasPathOpdCapability,
    hasPathIpdCapability,
  ]);

  useEffect(() => {
    let active = true;
    const patientIds = [
      ...(path_lab_test || []).map((prescription) => prescription?.patientId),
      ...(bookingSourceRef.current || []).map((prescription) => prescription?.patientId),
      ...(ipdWorkflowGroups || []).map((group) => group?.patientId),
    ];
    listLabBookingsForPatients(patientIds)
      .then((bookings) => {
        if (!active) return;
        const source = bookingSourceRef.current || [];
        const matcher = createPatientBookedTestMatcher(bookings, [
          ...(path_lab_test || []),
          ...source,
        ]);
        setIsTestBookedByPatient(() => matcher);
      })
      .catch(() => {
        if (active) setIsTestBookedByPatient(() => () => false);
      });
    return () => {
      active = false;
    };
  }, [path_lab_test, ipdWorkflowGroups]);

  const isQueueLoading =
    (hasPathOpdCapability && fetching.path_lab_test) ||
    (hasPathIpdCapability && fetchingIpd);

  const handleRefreshQueue = async () => {
    const tasks = [];
    if (hasPathOpdCapability) tasks.push(fetchPathLabTestList());
    if (hasPathIpdCapability) tasks.push(fetchIpdWorkflowGroups());
    await Promise.all(tasks);
  };

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="flex-end"
        mb={1}
      >
        <IconButton onClick={handleRefreshQueue}>
          <Refresh />
        </IconButton>
      </Grid>
      {!capabilitiesLoading && !hasQueueAccess && (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          height={"200px"}
          marginTop={"20px"}
          width={"100%"}
          backgroundColor={colors.primary[400]}
        >
          <Typography variant="body1">Pathology Admin access is not available for your subscription package.</Typography>
        </Box>
      )}
      {hasQueueAccess && (
        <Box sx={{ width: "100%", pr: 1 }}>
          {isQueueLoading && combinedQueueItems.length === 0 ? (
            <Box
              display={"flex"}
              height={"400px"}
              justifyContent={"center"}
              alignItems={"center"}
              width={"100%"}
            >
              <CircularProgress size={80} />
            </Box>
          ) : combinedQueueItems.length > 0 ? (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              {combinedQueueItems.map((item) => (
                <Grid item {...QUEUE_TILE_GRID_PROPS} key={item.key}>
                  {item.type === "opd" ? (
                    <OpdPrescriptionWorkflowCard
                      prescription={item.prescription}
                      onBill={handleAccept}
                      onRefresh={fetchPathLabTestList}
                      patientTypeLabel="OPD"
                      isTestBookedByPatient={(test) =>
                        isTestBookedByPatient(test, item.prescription)
                      }
                    />
                  ) : (
                    <IpdPatientWorkflowCard
                      group={item.group}
                      onRefresh={fetchIpdWorkflowGroups}
                      patientTypeLabel="IPD"
                      isTestBookedByPatient={(test) => isTestBookedByPatient(test, item.group)}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              height={"200px"}
              marginTop={"20px"}
              width={"100%"}
              backgroundColor={colors.primary[400]}
            >
              <Typography variant="body1">No patients in queue.</Typography>
            </Box>
          )}
        </Box>
      )}
      <Calendar
        open={open}
        onClose={() => {
          setOpen(false);
          setLabTestInfo(null);
        }}
        appointment={{
          patientId: lab_test_info?.patientId,
          patientName: lab_test_info?.patientName,
          doctorId: lab_test_info?.doctorId,
          doctorName: lab_test_info?.doctorName,
        }}
      />

      <InvoiceModal
        open={openInvoiceModal}
        onClose={() => setOpenInvoiceModal(false)}
        invoiceDetails={invoiceDetails}
        reportId={selectedTest?.reportId}
        test={selectedTest}
        refetchPathLabTestList={fetchPathLabTestList}
      />

    </Grid>
  );
};

export default PendingPatientTest;
